/**
 * JWT/session → frozen `ExperienceRoleId` bridge (N1 role landing activation).
 *
 * Mapping rules (no invented roles):
 * 1. Exact passthrough — if JWT `role` is already one of the 10 frozen
 *    `ExperienceRoleId` values, consume it. This is not invention; it is the
 *    runtime bridge for actors already tagged with the frozen vocabulary.
 *    - internal experience roles require `entityType === 'internal'`
 *    - `external_customer` / `external_supplier` require `entityType === 'external'`
 * 2. Technical identity mapping — JWT `role === 'owner'` or
 *    `permissions` containing `'system:admin'` map to `'tenant_admin'`
 *    (same authority already used by `business-menu-rbac` full-menu grant).
 *    Technical identities must not replace role-specific proof for the other
 *    nine frozen roles — those require exact passthrough (rule 1).
 * 3. Everything else → `null` (fail-closed). Callers MUST fall back to the
 *    pre-activation conservative default, never a more privileged landing.
 *
 * `external` plane separation is still owned by `BusinessPlaneGuard` (Portal);
 * this resolver only runs inside MainRouter after that guard admits the actor.
 */
import type { ExperienceRoleId } from '@shared/frontend-experience-architecture-and-navigation-canon';
import { ROLE_NAVIGATION_MATRIX_V0 } from '@shared/frontend-experience-architecture-and-navigation-canon';

export interface ExperienceRoleResolutionInput {
  entityType?: 'internal' | 'external' | 'user' | null;
  role?: string;
  permissions?: readonly string[];
}

const OWNER_ROLE = 'owner';
const SYSTEM_ADMIN_PERMISSION = 'system:admin';

const FROZEN_EXPERIENCE_ROLE_IDS: ReadonlySet<string> = new Set(
  ROLE_NAVIGATION_MATRIX_V0.map((row) => row.roleId)
);

const EXTERNAL_EXPERIENCE_ROLES: ReadonlySet<ExperienceRoleId> = new Set([
  'external_customer',
  'external_supplier',
]);

/**
 * Resolves a JWT/session payload to a frozen `ExperienceRoleId`, or `null`
 * when unresolved. Callers MUST treat `null` as fail-closed — i.e. fall back
 * to the same conservative landing/visibility that existed before role-matrix
 * activation, never a more privileged one.
 */
export function resolveExperienceRoleId(
  payload: ExperienceRoleResolutionInput | null | undefined
): ExperienceRoleId | null {
  if (!payload) {
    return null;
  }

  const role = payload.role;
  if (role && FROZEN_EXPERIENCE_ROLE_IDS.has(role)) {
    const experienceRole = role as ExperienceRoleId;
    if (EXTERNAL_EXPERIENCE_ROLES.has(experienceRole)) {
      return payload.entityType === 'external' ? experienceRole : null;
    }
    return payload.entityType === 'internal' ? experienceRole : null;
  }

  if (payload.entityType !== 'internal') {
    return null;
  }
  if (role === OWNER_ROLE) {
    return 'tenant_admin';
  }
  if ((payload.permissions ?? []).includes(SYSTEM_ADMIN_PERMISSION)) {
    return 'tenant_admin';
  }
  return null;
}
